'use client';

import { useDebounce } from '@frontend/client/hooks';
import {
  useSignedPreferences,
  useSignPreferences,
} from '@frontend/client/hooks/useSignedPreferences';
import {
  SUPPORTED_CHAINS,
  USD_STABLE_SYMBOLS,
} from '@frontend/common/constants';
import JustanameAddressInput from '@frontend/components/JustanameAddressInput';
import { restrictedUsernames } from '@frontend/shared/validation';
import { safeWindow } from '@frontend/utils/safeWindow';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useIsSubnameAvailable,
  usePrimaryName,
  useRevokeSubname,
} from '@justaname.id/react';
import { ArrowLeftIcon, CheckIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex } from '@radix-ui/themes';
import { getChain } from '@yodlpay/tokenlists';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isAddress } from 'viem';
import { useAccount, useSwitchChain } from 'wagmi';
import { z } from 'zod';

export const usernameSchema = z
  .string()
  .min(6, { message: 'Username must be at least 6 characters' })
  .regex(/^[a-zA-Z0-9_]+$/, {
    message: 'Username can only contain letters, numbers, and underscores',
  })
  .refine((value) => !isAddress(value), {
    message: 'Username cannot be an Ethereum address',
  })
  .refine((value) => !restrictedUsernames.includes(value), {
    message: 'Username is not allowed',
  });

const createPaymentPreferencesSchema = (hasPrimaryName: boolean) =>
  z.object({
    username: hasPrimaryName ? z.string() : usernameSchema,
    tokens: z.array(z.string()).min(1, 'At least one token must be selected'),
    chains: z.array(z.string()).min(1, 'At least one chain must be selected'),
  });

type PaymentPreferencesForm = z.infer<
  ReturnType<typeof createPaymentPreferencesSchema>
>;

// Deduplicate tokens by symbol to avoid multiple cards for same token
const TOKENS = USD_STABLE_SYMBOLS.filter(
  (token, index, array) =>
    array.findIndex((t) => t.symbol === token.symbol) === index,
);

const SelectableCard = ({
  id,
  isSelected,
  onClick,
  title,
  iconUrl,
}: {
  id: string;
  isSelected: boolean;
  onClick: () => void;
  title: string;
  iconUrl?: string;
}) => {
  const selectionStyle = {
    border: '1px solid #9D7BFC',
    borderImageSlice: 1,
    boxShadow: '0px 0px 14px 0px #6C46D066',
  };

  return (
    <Card
      key={id}
      variant={isSelected ? 'surface' : 'classic'}
      className="cursor-pointer"
      style={isSelected ? selectionStyle : {}}
      onClick={onClick}
    >
      <Flex direction="column" align="center" justify="center" gap="2" p="1">
        <div className="w-11 h-11 rounded-full flex items-center justify-center">
          {iconUrl && (
            <Image
              src={iconUrl}
              alt={title}
              className="w-11 h-11 rounded-full"
              width={44}
              height={44}
            />
          )}
        </div>
        <span className="text-sm font-medium text-center">{title}</span>
      </Flex>
    </Card>
  );
};

const StageLayout = ({
  title,
  subtitle,
  children,
  onNext,
  canProceed,
  buttonText = 'Continue',
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onNext: () => void;
  canProceed: boolean;
  buttonText?: string;
}) => (
  <>
    <h2 className="text-2xl font-medium mb-3">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 mb-7">{subtitle}</p>}

    <div className={subtitle ? 'mb-6' : 'mb-6 mt-7'}>{children}</div>

    <Button
      onClick={onNext}
      disabled={!canProceed}
      className="!w-full"
      size="3"
      radius="full"
    >
      {buttonText}
    </Button>
  </>
);

interface PaymentPreferencesSectionProps {
  primaryName?: string;
  existingPreferences?: any;
  isPreferencesLoading?: boolean;
  onBack?: () => void;
  onClose?: () => void;
}

const PaymentPreferencesSection = ({
  primaryName: propPrimaryName,
  existingPreferences: propExistingPreferences,
  isPreferencesLoading: propIsPreferencesLoading = false,
  onBack,
  onClose,
}: PaymentPreferencesSectionProps = {}) => {
  const { chain, address } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const [isLoading] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine if we need to use fallback hooks
  const needsFallbackData = !propPrimaryName && !propExistingPreferences;

  // Fallback hooks for when props are not provided (backward compatibility)
  const { primaryName: fallbackPrimaryName } = usePrimaryName({
    address,
    enabled: needsFallbackData,
  });

  const { preferences: fallbackPreferences, isLoading: fallbackIsLoading } =
    useSignedPreferences({
      addressOrEns: address,
    });

  // Use props if provided, otherwise use fallback hooks
  const primaryName = propPrimaryName || fallbackPrimaryName;
  const existingPreferences = propExistingPreferences || fallbackPreferences;
  const isPreferencesLoading = needsFallbackData
    ? fallbackIsLoading
    : propIsPreferencesLoading;

  // Initialize step to 1, will be updated in useEffect when primaryName is available
  const [currentStep, setCurrentStep] = useState(1);
  const [hasExistingPreferences, setHasExistingPreferences] = useState(false);

  // ALL hooks must be called before any conditional returns

  const form = useForm<PaymentPreferencesForm>({
    resolver: zodResolver(createPaymentPreferencesSchema(!!primaryName)),
    defaultValues: {
      username: '',
      tokens: [],
      chains: SUPPORTED_CHAINS.map((chain) => chain.name),
    },
  });

  const { watch, setValue, handleSubmit, getValues, formState, trigger } = form;
  const formData = watch();
  const { errors } = formState;

  const { debouncedValue: debouncedUsername } = useDebounce(
    formData.username,
    500,
  );

  // Validate username after debounce (only if user doesn't have primary name)
  useEffect(() => {
    if (
      debouncedUsername &&
      formData.username === debouncedUsername &&
      !primaryName
    ) {
      trigger('username');
    }
  }, [debouncedUsername, formData.username, trigger, primaryName]);

  // Populate form with existing preferences and determine starting step
  useEffect(() => {
    if (existingPreferences && !isPreferencesLoading) {
      // Map chain IDs back to chain names, only if chainIds exist and have values
      const chainNames =
        existingPreferences.chainIds && existingPreferences.chainIds.length > 0
          ? existingPreferences.chainIds
              .map(
                (chainId) =>
                  SUPPORTED_CHAINS.find((chain) => chain.id === chainId)?.name,
              )
              .filter(Boolean)
          : [];

      // Check if user has existing preferences (tokens or chains)
      const hasTokens =
        existingPreferences.tokenSymbols &&
        existingPreferences.tokenSymbols.length > 0;
      const hasChains = chainNames.length > 0;
      const hasPrefs = hasTokens || hasChains;

      setHasExistingPreferences(hasPrefs);

      // Set form values only if existing data exists
      if (hasTokens) {
        setValue('tokens', existingPreferences.tokenSymbols);
      }

      if (hasChains) {
        setValue('chains', chainNames as string[]);
      }

      // Determine starting step based on existing preferences
      if (primaryName) {
        setValue('username', primaryName);
        // If user has existing preferences, go straight to summary
        if (hasPrefs) {
          setCurrentStep(4);
        } else {
          setCurrentStep(2);
        }
      } else if (hasPrefs) {
        // No primary name but has preferences, start at step 1 but will show summary when done
        setCurrentStep(1);
      }
    }
  }, [existingPreferences, isPreferencesLoading, primaryName, setValue]);

  const { isSubnameAvailable } = useIsSubnameAvailable({
    username: debouncedUsername,
    enabled: !primaryName, // Don't check availability if user already has primary name
  });

  const { mutate } = useSignPreferences({
    username: formData.username,
    isRegistered: !!primaryName,
    onError: (error: any) => {
      setMutationError(
        error?.message || 'An error occurred while saving preferences',
      );
      setIsSubmitting(false);
    },
    onSuccess: () => {
      setMutationError(null);
      setIsSubmitting(false);
      if (onClose) {
        onClose();
      } else {
        safeWindow?.location.reload();
      }
    },
  });

  const { revokeSubname, isRevokeSubnamePending } = useRevokeSubname();

  const handleRevokeSubname = async () => {
    if (!primaryName?.endsWith('.yodl.eth')) return;
    try {
      const username = primaryName.replace('.yodl.eth', '');
      await revokeSubname({
        username,
        ensDomain: 'yodl.eth',
      });
      if (onClose) {
        onClose();
      } else {
        safeWindow?.location.reload();
      }
    } catch (error) {
      console.error('Failed to revoke subdomain:', error);
    }
  };

  const getButtonText = useMemo(() => {
    if (!formData.username) return 'Continue';
    if (formData.username !== debouncedUsername) return 'Checking...';
    if (isSubnameAvailable?.isAvailable === false) return 'Name not available';
    return 'Continue';
  }, [formData.username, debouncedUsername, isSubnameAvailable]);

  const handleNext = () => {
    if (currentStep < 4) {
      // If user has existing preferences and completes a stage, skip to summary
      if (hasExistingPreferences && currentStep < 3) {
        setCurrentStep(4);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > (primaryName ? 2 : 1)) {
      setCurrentStep(currentStep - 1);
    } else if (onBack) {
      onBack();
    }
  };

  const onSubmit = async (data: PaymentPreferencesForm) => {
    setIsSubmitting(true);
    setMutationError(null);

    // Check if user is on mainnet (chain ID 1)
    if (chain?.id !== 1) {
      try {
        await switchChainAsync({ chainId: 1 });
      } catch (error) {
        setMutationError('Failed to switch to mainnet. Please try again.');
        setIsSubmitting(false);
        return;
      }
    }

    const mappedChains = data.chains
      .map((chainName) => {
        return SUPPORTED_CHAINS.find((chain) => chain.name === chainName)?.id;
      })
      .filter((chainId) => chainId !== undefined);

    const mappedTokens = data.tokens
      .map((tokenSymbol) => {
        return TOKENS.find((token) => token.symbol === tokenSymbol)?.symbol;
      })
      .filter((tokenSymbol) => tokenSymbol !== undefined);

    mutate({
      chains: mappedChains,
      tokens: mappedTokens,
    });
  };

  const toggleSelection = (value: string, field: 'tokens' | 'chains') => {
    const current = getValues(field);
    const updated = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    setValue(field, updated, { shouldValidate: false });
  };

  const renderStageContent = () => {
    switch (currentStep) {
      case 1:
        // If user has primary name, they shouldn't see this stage
        if (primaryName) {
          return null;
        }
        return (
          <Stage1
            username={formData.username}
            onUsernameChange={(value) =>
              setValue('username', value, { shouldValidate: false })
            }
            isLoading={isLoading}
            onNext={handleNext}
            canProceed={
              !!formData.username &&
              !errors.username &&
              formData.username === debouncedUsername &&
              !!isSubnameAvailable?.isAvailable
            }
            buttonText={getButtonText}
            error={errors.username?.message}
          />
        );
      case 2:
        return (
          <Stage2
            selectedTokens={formData.tokens}
            onTokenToggle={(tokenSymbol) =>
              toggleSelection(tokenSymbol, 'tokens')
            }
            onNext={handleNext}
            canProceed={formData.tokens.length > 0}
          />
        );
      case 3:
        return (
          <Stage3
            selectedChains={formData.chains}
            onChainToggle={(chain) => toggleSelection(chain, 'chains')}
            onNext={handleNext}
            canProceed={formData.chains.length > 0}
          />
        );
      case 4:
        return (
          <SummaryStage
            formData={formData}
            onSubmit={handleSubmit(onSubmit)}
            onEdit={(stage) => setCurrentStep(stage)}
            canEditName={!primaryName}
            errors={errors}
            mutationError={mutationError}
            isSubmitting={isSubmitting}
            onRetry={() => {
              setMutationError(null);
              handleSubmit(onSubmit)();
            }}
            primaryName={primaryName}
            onRevokeSubname={handleRevokeSubname}
            isRevokeSubnamePending={isRevokeSubnamePending}
          />
        );
      default:
        return null;
    }
  };

  // Show loading state if we're still waiting for essential data OR if we have primaryName but currentStep hasn't been updated yet
  const isStillLoading =
    (isPreferencesLoading && !existingPreferences && !primaryName) ||
    (primaryName && currentStep === 1);

  if (isStillLoading) {
    return (
      <div className="max-w-md mx-auto flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto overflow-y-auto max-h-[80vh]">
      <div className="flex flex-col gap-2 mb-3 items-center">
        <ProgressBar
          currentStep={primaryName ? currentStep - 1 : currentStep}
          totalSteps={primaryName ? 3 : 4}
          onBack={
            currentStep > (primaryName ? 2 : 1) || onBack
              ? handleBack
              : undefined
          }
        />
      </div>

      {renderStageContent()}
    </div>
  );
};

const Stage1 = ({
  username,
  onUsernameChange,
  isLoading,
  onNext,
  canProceed,
  buttonText,
  error,
}: {
  username: string;
  onUsernameChange: (value: string) => void;
  isLoading: boolean;
  onNext: () => void;
  canProceed: boolean;
  buttonText: string;
  error?: string;
}) => (
  <StageLayout
    title="Create your ENS handle"
    subtitle="Create your ENS handle to receive payments with your name."
    onNext={onNext}
    canProceed={canProceed}
    buttonText={buttonText}
  >
    <div>
      <JustanameAddressInput
        isLoading={isLoading}
        value={username}
        onUsernameChange={onUsernameChange}
        placeholder="Enter your handle"
      />
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
    </div>
  </StageLayout>
);

const Stage2 = ({
  selectedTokens,
  onTokenToggle,
  onNext,
  canProceed,
}: {
  selectedTokens: string[];
  onTokenToggle: (tokenSymbol: string) => void;
  onNext: () => void;
  canProceed: boolean;
}) => (
  <StageLayout
    title="Set token preferences"
    subtitle="Choose which tokens you prefer to receive. Payments will be automatically converted to your preferred token."
    onNext={onNext}
    canProceed={canProceed}
    buttonText="Continue"
  >
    <div className="grid grid-cols-4 gap-3">
      {TOKENS.map((token) => {
        const isSelected = selectedTokens.includes(token.symbol);
        return (
          <SelectableCard
            key={token.symbol}
            id={token.symbol}
            isSelected={isSelected}
            onClick={() => onTokenToggle(token.symbol)}
            title={token.symbol}
            iconUrl={token.logoUri}
          />
        );
      })}
    </div>
  </StageLayout>
);

const Stage3 = ({
  selectedChains,
  onChainToggle,
  onNext,
  canProceed,
}: {
  selectedChains: string[];
  onChainToggle: (chain: string) => void;
  onNext: () => void;
  canProceed: boolean;
}) => (
  <StageLayout
    title="Set chain preferences"
    subtitle="By default, all chains are selected. Deselect any chains you want to exclude."
    onNext={onNext}
    canProceed={canProceed}
    buttonText="Continue"
  >
    <div className="grid grid-cols-3 gap-3">
      {SUPPORTED_CHAINS.map((chain) => {
        const isSelected = selectedChains.includes(chain.name);
        const info = getChain(chain.id);
        return (
          <SelectableCard
            id={chain.name}
            key={chain.name}
            isSelected={isSelected}
            onClick={() => onChainToggle(chain.name)}
            title={info?.chainName}
            iconUrl={info?.logoUri}
          />
        );
      })}
    </div>
  </StageLayout>
);

const SummaryStage = ({
  formData,
  onSubmit,
  onEdit,
  canEditName = true,
  errors,
  mutationError,
  isSubmitting,
  onRetry,
  primaryName,
  onRevokeSubname,
  isRevokeSubnamePending,
}: {
  formData: PaymentPreferencesForm;
  onSubmit: () => void;
  onEdit: (stage: number) => void;
  canEditName?: boolean;
  errors?: any;
  mutationError?: string | null;
  isSubmitting?: boolean;
  onRetry?: () => void;
  primaryName?: string;
  onRevokeSubname?: () => void;
  isRevokeSubnamePending?: boolean;
}) => {
  // Log form errors if any exist
  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      console.log('Form errors in summary:', errors);
    }
  }, [errors]);

  return (
    <StageLayout
      title="Ready to receive payments"
      onNext={mutationError && onRetry ? onRetry : onSubmit}
      canProceed={!isSubmitting}
      buttonText={
        mutationError
          ? 'Retry'
          : isSubmitting
            ? 'Saving...'
            : 'Save Preferences'
      }
    >
      <div className="space-y-4">
        {mutationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="text-red-600 font-medium text-sm">Error</div>
            </div>
            <p className="text-red-700 text-sm mt-1">{mutationError}</p>
          </div>
        )}
        <div className="flex items-center gap-3">
          <CheckIcon
            className="flex-shrink-0 w-7 h-7"
            style={{ color: '#6AC770' }}
          />
          <div className="flex-1">
            <p className="font-medium mb-3 text-base">ENS Handle:</p>
            <div className="flex justify-between items-center">
              <p className="text-gray-600 text-sm">
                {formData.username.includes('.')
                  ? formData.username
                  : `${formData.username}.yodl.eth`}
              </p>
              <div className="flex gap-2">
                {canEditName && (
                  <button
                    onClick={() => onEdit(1)}
                    className="text-sm underline hover:opacity-80"
                    style={{ color: '#BEBEBF' }}
                  >
                    Edit
                  </button>
                )}
                {primaryName?.endsWith('.yodl.eth') && onRevokeSubname && (
                  <button
                    onClick={onRevokeSubname}
                    disabled={isRevokeSubnamePending}
                    className="text-sm underline hover:opacity-80 text-red-500 disabled:opacity-50"
                  >
                    {isRevokeSubnamePending ? 'Revoking...' : 'Revoke'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="border-t pt-4 flex items-center gap-3"
          style={{ borderColor: '#3A3C3E' }}
        >
          <CheckIcon
            className="flex-shrink-0 w-7 h-7"
            style={{ color: '#6AC770' }}
          />
          <div className="flex-1">
            <p className="font-medium mb-3 text-base">Accepted Tokens:</p>
            <div className="flex justify-between items-center">
              <div className="flex flex-wrap" style={{ gap: '6px' }}>
                {formData.tokens.map((tokenSymbol) => {
                  const token = TOKENS.find((t) => t.symbol === tokenSymbol);
                  return token?.logoUri ? (
                    <Image
                      key={tokenSymbol}
                      src={token.logoUri}
                      alt={tokenSymbol}
                      className="rounded-full"
                      width={32}
                      height={32}
                    />
                  ) : null;
                })}
              </div>
              <button
                onClick={() => onEdit(2)}
                className="text-sm underline hover:opacity-80 ml-3"
                style={{ color: '#BEBEBF' }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>

        <div
          className="border-t pt-4 flex items-center gap-3"
          style={{ borderColor: '#3A3C3E' }}
        >
          <CheckIcon
            className="flex-shrink-0 w-7 h-7"
            style={{ color: '#6AC770' }}
          />
          <div className="flex-1">
            <p className="font-medium mb-3 text-base">Supported Chains:</p>
            <div className="flex justify-between items-center">
              <div className="flex flex-wrap" style={{ gap: '6px' }}>
                {formData.chains.map((chainName) => {
                  const chain = SUPPORTED_CHAINS.find(
                    (c) => c.name === chainName,
                  );
                  const info = chain ? getChain(chain.id) : null;
                  return info?.logoUri ? (
                    <Image
                      key={chainName}
                      src={info.logoUri}
                      alt={chainName}
                      className="rounded-full"
                      width={32}
                      height={32}
                    />
                  ) : null;
                })}
              </div>
              <button
                onClick={() => onEdit(3)}
                className="text-sm underline hover:opacity-80 ml-3"
                style={{ color: '#BEBEBF' }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    </StageLayout>
  );
};

const ProgressBar = ({
  currentStep,
  totalSteps,
  onBack,
}: {
  currentStep: number;
  totalSteps: number;
  onBack?: () => void;
}) => {
  return (
    <div className="flex items-center gap-3 mb-6 w-full">
      {onBack && (
        <Button variant="ghost" size="2" onClick={onBack} className="p-2">
          <ArrowLeftIcon />
        </Button>
      )}

      <div className="flex gap-2 flex-1 justify-center">
        {Array.from({ length: totalSteps }, (_, index) => (
          <div
            key={index}
            className={`h-1 rounded-full transition-all duration-300 ease-in-out ${
              index < currentStep ? 'bg-[#925EFC]' : 'bg-[#303234]'
            }`}
            style={{ width: '40px' }}
          />
        ))}
      </div>

      {onBack && <div className="w-8" />}
    </div>
  );
};

export default PaymentPreferencesSection;
